// ============================================================================
// GitHub 개발 현황 패널 (CHG-046: REQ-V46-013)
// 이슈 상세 화면에 표시. 프로젝트에 Repo 연결이 없으면 렌더링하지 않음.
// ============================================================================

import { useState } from 'react';
import {
  GitBranch, GitCommit, GitPullRequest, Copy, Check,
  Plus, ExternalLink, AlertTriangle, ChevronDown, Wrench,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGitHubRepos,
  useIssueBranches,
  useIssueCommits,
  useIssuePullRequests,
} from '../../hooks/useGitHub';
import type { GitHubPRItem, GitHubPRReviewItem } from '../../services/githubService';
import BranchCreateModal from './BranchCreateModal';
import PRCreateModal from './PRCreateModal';
import WorkflowSection from './WorkflowSection';

interface Props {
  projectId: string;
  issueId: string; // Issue PK (cuid)
  issueKey: string; // e.g. "TRACK-123"
}

export default function GitHubDevPanel({ projectId, issueId, issueKey }: Props) {
  const { data: repos } = useGitHubRepos(projectId);
  const { data: branches } = useIssueBranches(projectId, issueId);
  const { data: commits } = useIssueCommits(projectId, issueId);
  const { data: prs } = useIssuePullRequests(projectId, issueId);

  const [showAllCommits, setShowAllCommits] = useState(false);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [showPRModal, setShowPRModal] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // 프로젝트에 Repo 연결이 없으면 패널을 렌더링하지 않음
  if (!repos || repos.length === 0) return null;

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('클립보드에 복사되었습니다.');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('복사에 실패했습니다.');
    }
  };

  const displayedCommits = showAllCommits ? commits : commits?.slice(0, 5);

  return (
    <div className="border-t border-gray-100 px-5 py-3">
      <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
        개발 현황
      </h4>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        <button
          onClick={() => copyToClipboard(`[${issueKey}] `, 'commit')}
          className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
        >
          {copiedField === 'commit' ? <Check className="size-3" /> : <Copy className="size-3" />}
          커밋 접두사
        </button>
        <button
          onClick={() => setShowBranchModal(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-sm text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
        >
          <Plus className="size-3" />
          브랜치
        </button>
        <button
          onClick={() => setShowPRModal(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-sm text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
        >
          <Plus className="size-3" />
          PR
        </button>
      </div>

      {/* Branches */}
      {branches && branches.length > 0 && (
        <Section icon={<GitBranch className="size-3.5" />} title="브랜치" count={branches.length}>
          {branches.map((b) => (
            <div key={b.branchId} className="flex items-center gap-2 py-1.5">
              <a
                href={b.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline truncate flex-1 font-mono"
              >
                {b.name}
              </a>
              <BranchStatusBadge status={b.status} />
              <button
                onClick={() => copyToClipboard(b.name, b.branchId)}
                className="p-0.5 text-gray-400 hover:text-gray-600 shrink-0"
                title="브랜치명 복사"
              >
                {copiedField === b.branchId ? <Check className="size-3" /> : <Copy className="size-3" />}
              </button>
            </div>
          ))}
        </Section>
      )}

      {/* Pull Requests */}
      {prs && prs.length > 0 && (
        <Section icon={<GitPullRequest className="size-3.5" />} title="Pull Requests" count={prs.length}>
          {prs.map((pr) => (
            <PRItem key={pr.prId} pr={pr} />
          ))}
        </Section>
      )}

      {/* Commits */}
      {commits && commits.length > 0 && (
        <Section icon={<GitCommit className="size-3.5" />} title="커밋" count={commits.length}>
          {displayedCommits?.map((c) => (
            <div key={c.commitId} className="flex items-start gap-2 py-1.5">
              <div className="flex items-center gap-1 shrink-0">
                {c.linkType === 'FIXES' && <span title="FIXES"><Wrench className="size-3 text-orange-500" /></span>}
                <a
                  href={c.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline font-mono"
                >
                  {c.shortSha}
                </a>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-700 truncate">{c.message.split('\n')[0]}</div>
                <div className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5">
                  {c.author.maiosUser ? (
                    <span>{c.author.maiosUser.name}</span>
                  ) : (
                    <span>{c.author.name}</span>
                  )}
                  <span>·</span>
                  <span>{formatRelative(c.committedAt)}</span>
                </div>
              </div>
            </div>
          ))}
          {commits.length > 5 && !showAllCommits && (
            <button
              onClick={() => setShowAllCommits(true)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mt-1"
            >
              <ChevronDown className="size-3" />
              {commits.length - 5}개 더보기
            </button>
          )}
        </Section>
      )}

      {/* CI/CD Workflows (CHG-048) */}
      <WorkflowSection projectId={projectId} issueId={issueId} />

      {/* Modals */}
      {showBranchModal && (
        <BranchCreateModal
          projectId={projectId}
          issueId={issueId}
          repos={repos}
          onClose={() => setShowBranchModal(false)}
        />
      )}
      {showPRModal && (
        <PRCreateModal
          projectId={projectId}
          issueId={issueId}
          repos={repos}
          branches={branches ?? []}
          onClose={() => setShowPRModal(false)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function Section({ icon, title, count, children }: {
  icon: React.ReactNode;
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 text-sm font-medium text-gray-500 mb-1">
        {icon} {title} <span className="text-gray-400">({count})</span>
      </div>
      <div className="ml-1 border-l-2 border-gray-100 pl-3">
        {children}
      </div>
    </div>
  );
}

function BranchStatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string }> = {
    ACTIVE: { bg: 'bg-green-100', text: 'text-green-700' },
    MERGED: { bg: 'bg-purple-100', text: 'text-purple-700' },
    DELETED: { bg: 'bg-gray-100', text: 'text-gray-500' },
  };
  const c = config[status] ?? config.ACTIVE;
  return (
    <span className={`px-1.5 py-0.5 text-[11px] font-medium rounded ${c.bg} ${c.text}`}>
      {status}
    </span>
  );
}

function PRItem({ pr }: { pr: GitHubPRItem }) {
  const statusConfig: Record<string, { bg: string; text: string }> = {
    OPEN: { bg: 'bg-green-100', text: 'text-green-700' },
    MERGED: { bg: 'bg-purple-100', text: 'text-purple-700' },
    CLOSED: { bg: 'bg-red-100', text: 'text-red-700' },
    DRAFT: { bg: 'bg-gray-100', text: 'text-gray-500' },
  };
  const sc = statusConfig[pr.status] ?? statusConfig.OPEN;

  return (
    <div className="py-1.5">
      <div className="flex items-start gap-2">
        <a
          href={pr.githubUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline flex-1 min-w-0"
        >
          <span className="font-medium">#{pr.prNumber}</span>{' '}
          <span className="text-gray-700">{pr.title}</span>
          <ExternalLink className="size-3 inline ml-1 text-gray-400" />
        </a>
        <span className={`px-1.5 py-0.5 text-[11px] font-medium rounded shrink-0 ${sc.bg} ${sc.text}`}>
          {pr.status}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-400">
        {/* Stats */}
        <span className="text-green-600">+{pr.additions}</span>
        <span className="text-red-500">-{pr.deletions}</span>
        <span>{pr.filesChanged} files</span>

        {/* CI */}
        {pr.ciStatus && <CIBadge status={pr.ciStatus} />}

        {/* Conflicts */}
        {pr.hasConflicts && (
          <span className="flex items-center gap-0.5 text-amber-600">
            <AlertTriangle className="size-3" /> 충돌
          </span>
        )}
      </div>

      {/* Reviews */}
      {pr.reviews.length > 0 && (
        <div className="flex items-center gap-1 mt-1">
          {pr.reviews.map((r, i) => (
            <ReviewBadge key={i} review={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function CIBadge({ status }: { status: string }) {
  switch (status) {
    case 'SUCCESS': return <span className="text-green-600">✅</span>;
    case 'FAILURE': return <span className="text-red-500">❌</span>;
    case 'PENDING': return <span className="text-amber-500">⏳</span>;
    case 'ERROR': return <span className="text-red-500">⚠️</span>;
    default: return null;
  }
}

function ReviewBadge({ review }: { review: GitHubPRReviewItem }) {
  const stateIcon: Record<string, string> = {
    PENDING: '⏳',
    APPROVED: '✅',
    CHANGES_REQUESTED: '❌',
    COMMENTED: '💬',
    DISMISSED: '➖',
  };
  const name = review.reviewerUser?.name ?? review.reviewerGithub;
  const initial = name.charAt(0).toUpperCase();

  return (
    <div className="flex items-center gap-0.5" title={`${name}: ${review.state}`}>
      <div className="size-5 rounded-full bg-gray-200 text-gray-600 text-[11px] font-medium flex items-center justify-center">
        {initial}
      </div>
      <span className="text-[11px]">{stateIcon[review.state] ?? '❓'}</span>
    </div>
  );
}

function formatRelative(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return '방금 전';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}시간 전`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}일 전`;
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}
